/**
 * DiscoveryBaselineTab — Phase 10 (Validación Humana)
 *
 * Materializes "Human-Governed QA" in the platform UI. Shows the EvidenceRecords
 * persisted to the relational schema by Phase 9, and lets a QA validate or
 * reject them. Verified evidence becomes the gateway for STRICT enterprise
 * generation (Phase 10.4 — Gating).
 *
 * Spec: DISCOVERY_SPEC.md § 10.1 — § 10.4
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
    AlertCircle, CheckCircle2, XCircle, Clock, RefreshCw, AlertTriangle,
    Eye, ShieldCheck, ShieldX, Filter, Layers, Activity,
} from 'lucide-react';
import { api } from '../../services/api';
import EvidenceDetailModal from './EvidenceDetailModal';

interface DiscoveryBaselineTabProps {
    entityId: string;
    projectId: string;
}

interface EvidenceRow {
    id: string;
    evidence_id: string;
    session_id: string;
    screen_name: string;
    screen_fingerprint: string;
    action_type: string;
    action_selector: string | null;
    result_type: string;
    evidence_status: string;
    evidence_quality: string;
    capture_mode: string;
    screenshot_path: string | null;
    dom_elements: any;
    timestamp: string | null;
    verified_by: string | null;
    verified_at: string | null;
}

interface SessionInfo {
    source: 'relational' | 'legacy_jsonb' | 'none';
    id?: string;
    started_at?: string;
    finished_at?: string;
    duration_ms?: number;
    summary?: {
        total_records?: number;
        interactive_count?: number;
        total_known_elements?: number;
        total_screens?: number;
        result_type_breakdown?: Record<string, number>;
    };
    entity_id?: string;
    message?: string;
    warning?: string;
}

// ── Status / Quality visual helpers ──────────────────────────────────────────

const STATUS_BADGES: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
    captured:        { label: 'Capturado',       color: '#00B4D8', bg: '#00B4D822', icon: Activity },
    verified:        { label: 'Verificado',      color: '#06D6A0', bg: '#06D6A022', icon: ShieldCheck },
    rejected:        { label: 'Rechazado',       color: '#EF476F', bg: '#EF476F22', icon: XCircle },
    stale:           { label: 'Stale',           color: '#8899AA', bg: '#8899AA22', icon: Clock },
    needs_recapture: { label: 'Re-capturar',     color: '#FF8A3D', bg: '#FF8A3D22', icon: RefreshCw },
    partial:         { label: 'Parcial',         color: '#FFB703', bg: '#FFB70322', icon: AlertTriangle },
};

const QUALITY_TAGS: Record<string, { label: string; color: string }> = {
    complete:       { label: 'Completa',          color: '#06D6A0' },
    technical_only: { label: 'Solo técnica',      color: '#FFB703' },
    visual_only:    { label: 'Solo visual',       color: '#00B4D8' },
    incomplete:     { label: 'Incompleta',        color: '#EF476F' },
};

function StatusBadge({ status }: { status: string }) {
    const config = STATUS_BADGES[status] ?? STATUS_BADGES.captured;
    const Icon = config.icon;
    return (
        <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border"
            style={{ color: config.color, backgroundColor: config.bg, borderColor: `${config.color}66` }}
        >
            <Icon className="w-3 h-3" />
            {config.label}
        </span>
    );
}

function QualityPill({ quality }: { quality: string }) {
    const tag = QUALITY_TAGS[quality] ?? { label: quality, color: '#8899AA' };
    return (
        <span
            className="inline-block px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide"
            style={{ color: tag.color, backgroundColor: `${tag.color}22` }}
        >
            {tag.label}
        </span>
    );
}

// ── Main component ───────────────────────────────────────────────────────────

const DiscoveryBaselineTab: React.FC<DiscoveryBaselineTabProps> = ({ entityId }) => {
    const [session, setSession] = useState<SessionInfo | null>(null);
    const [records, setRecords] = useState<EvidenceRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedRecord, setSelectedRecord] = useState<EvidenceRow | null>(null);
    const [actionInProgress, setActionInProgress] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [sessionData, recordsData] = await Promise.all([
                api.getDiscoverySession(entityId),
                api.listEvidenceRecords(entityId, { limit: 200 }),
            ]);
            setSession(sessionData);
            setRecords(recordsData.records || []);
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Error cargando evidencia');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (entityId) fetchData();
    }, [entityId]);

    const filteredRecords = useMemo(() => {
        if (statusFilter === 'all') return records;
        return records.filter(r => r.evidence_status === statusFilter);
    }, [records, statusFilter]);

    const counts = useMemo(() => {
        const c: Record<string, number> = { all: records.length };
        for (const r of records) {
            c[r.evidence_status] = (c[r.evidence_status] || 0) + 1;
        }
        return c;
    }, [records]);

    const handleVerify = async (record: EvidenceRow, payload: any = {}) => {
        setActionInProgress(record.id);
        try {
            await api.verifyEvidence(record.id, payload);
            await fetchData();
            setSelectedRecord(null);
        } catch (e: any) {
            alert(`Error al verificar: ${e?.response?.data?.detail || e?.message}`);
        } finally {
            setActionInProgress(null);
        }
    };

    const handleReject = async (record: EvidenceRow, reason: string) => {
        setActionInProgress(record.id);
        try {
            await api.rejectEvidence(record.id, reason);
            await fetchData();
            setSelectedRecord(null);
        } catch (e: any) {
            alert(`Error al rechazar: ${e?.response?.data?.detail || e?.message}`);
        } finally {
            setActionInProgress(null);
        }
    };

    // ── Loading / empty states ──────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-slate-500">
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Cargando evidencia...
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                    <h4 className="text-red-400 font-medium">Error cargando evidencia</h4>
                    <p className="text-sm text-slate-400 mt-1">{error}</p>
                    <button
                        onClick={fetchData}
                        className="mt-3 px-3 py-1 bg-red-500/20 text-red-300 rounded text-sm hover:bg-red-500/30 transition-colors"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    if (session?.source === 'none') {
        return (
            <div className="text-center py-12">
                <Layers className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-slate-300 font-medium">Sin sesión de Discovery</h3>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                    Ejecuta <code className="px-1.5 py-0.5 bg-slate-800 rounded text-slate-300">
                        flowspecqa discover
                    </code> contra esta entidad para generar evidencia validable.
                </p>
            </div>
        );
    }

    // ── Layout ──────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* Session header card */}
            {session && session.source === 'relational' && (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                                Última sesión de Discovery
                            </div>
                            <div className="font-mono text-xs text-slate-400">
                                {session.id?.slice(0, 8)}
                                {session.started_at && (
                                    <span className="ml-2">
                                        · {new Date(session.started_at).toLocaleString('es-CL')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-right">
                            <div>
                                <div className="text-2xl font-bold text-white">
                                    {session.summary?.total_records ?? 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                                    Records
                                </div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-cyan-400">
                                    {session.summary?.total_screens ?? 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                                    Pantallas
                                </div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-emerald-400">
                                    {session.summary?.total_known_elements ?? 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                                    Selectores
                                </div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-amber-400">
                                    {session.summary?.interactive_count ?? 0}
                                </div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                                    Interactivos
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {session?.source === 'legacy_jsonb' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200">
                        <strong>Solo evidencia legacy:</strong> esta entidad tiene corpus en formato JSONB
                        antiguo. Ejecuta una nueva sesión de Discovery para poblar las tablas relacionales
                        y habilitar la validación humana.
                    </div>
                </div>
            )}

            {/* Filter bar */}
            <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-500 uppercase tracking-wide mr-2">Estado:</span>
                {(['all', 'captured', 'verified', 'rejected', 'needs_recapture', 'stale'] as const).map(s => {
                    const active = statusFilter === s;
                    const label = s === 'all' ? 'Todos' : (STATUS_BADGES[s]?.label ?? s);
                    const count = counts[s] ?? 0;
                    return (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1 rounded text-xs transition-colors ${
                                active
                                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                                    : 'bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-slate-800'
                            }`}
                        >
                            {label} <span className="opacity-60">{count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Records table */}
            {filteredRecords.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                    No hay evidencia con estado <strong>{statusFilter}</strong>.
                </div>
            ) : (
                <div className="border border-slate-800 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium w-20">Ev. ID</th>
                                <th className="text-left px-3 py-2 font-medium">Pantalla</th>
                                <th className="text-left px-3 py-2 font-medium">Acción</th>
                                <th className="text-left px-3 py-2 font-medium">Estado</th>
                                <th className="text-left px-3 py-2 font-medium">Calidad</th>
                                <th className="text-left px-3 py-2 font-medium">Validado por</th>
                                <th className="text-right px-3 py-2 font-medium">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRecords.map(r => (
                                <tr
                                    key={r.id}
                                    className="border-t border-slate-800 hover:bg-slate-900/40 transition-colors"
                                >
                                    <td className="px-3 py-2 font-mono text-xs text-cyan-400">
                                        {r.evidence_id}
                                    </td>
                                    <td className="px-3 py-2 text-slate-200 truncate max-w-xs">
                                        {r.screen_name}
                                    </td>
                                    <td className="px-3 py-2 text-slate-400 text-xs">
                                        <span className="font-mono bg-slate-800/50 px-1.5 py-0.5 rounded">
                                            {r.action_type}
                                        </span>
                                        {r.action_selector && (
                                            <span className="ml-1.5 text-slate-500">
                                                {r.action_selector}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <StatusBadge status={r.evidence_status} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <QualityPill quality={r.evidence_quality} />
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-400">
                                        {r.verified_by ? (
                                            <span title={r.verified_at ?? ''}>
                                                {r.verified_by}
                                            </span>
                                        ) : (
                                            <span className="text-slate-600">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-1.5 whitespace-nowrap">
                                        <button
                                            onClick={() => setSelectedRecord(r)}
                                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                                            title="Ver detalle"
                                        >
                                            <Eye className="w-3 h-3" />
                                        </button>
                                        {r.evidence_status !== 'verified' && (
                                            <button
                                                onClick={() => handleVerify(r)}
                                                disabled={actionInProgress === r.id}
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
                                                title="Validar para STRICT"
                                            >
                                                <ShieldCheck className="w-3 h-3" />
                                                Validar
                                            </button>
                                        )}
                                        {r.evidence_status !== 'rejected' && (
                                            <button
                                                onClick={() => {
                                                    const reason = window.prompt('Razón del rechazo:');
                                                    if (reason && reason.trim()) {
                                                        handleReject(r, reason.trim());
                                                    }
                                                }}
                                                disabled={actionInProgress === r.id}
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                                                title="Rechazar"
                                            >
                                                <ShieldX className="w-3 h-3" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                <span>
                    Cada evidencia validada habilita generación STRICT enterprise para los casos que
                    referencien sus selectores. Spec: <code>DISCOVERY_SPEC.md § 10.4</code>.
                </span>
            </div>

            {/* Detail modal */}
            {selectedRecord && (
                <EvidenceDetailModal
                    record={selectedRecord}
                    onClose={() => setSelectedRecord(null)}
                    onVerify={(payload) => handleVerify(selectedRecord, payload)}
                    onReject={(reason) => handleReject(selectedRecord, reason)}
                    actionInProgress={actionInProgress === selectedRecord.id}
                />
            )}
        </div>
    );
};

export default DiscoveryBaselineTab;
